<script setup lang="ts">
import type { User } from '@shared/types/models'
import type { FirebaseSignUpRequest, SignInMethod } from '@shared/types'
import firebase from 'firebase/app'
import { ref } from 'vue'
import getFirebaseUser from '@/firebase/getFirebaseUser'
import getAppleProvider from '@/firebase/getAppleProvider'
import getGoogleProvider from '@/firebase/getGoogleProvider'
import showToast from '@shared/modules/showToast'
import handleServerError from '@shared/modules/handleServerError'
import axios from 'axios'
import Spinner from '@shared/components/Spinner.vue'
import Tip from '@shared/components/Tip.vue'

type Props = {
    serviceName: SignInMethod
}

const { serviceName } = defineProps<Props>()
const isLoading = ref<boolean>(false)

function signIn(): void {
    isLoading.value = true

    let provider: firebase.auth.AuthProvider | null = null

    if (serviceName === 'apple') {
        provider = getAppleProvider()
    } else if (serviceName === 'google') {
        provider = getGoogleProvider()
    }

    if (!provider) {
        showToast({
            text: Lang.get('common.error_in_service') + ` ${serviceName}`,
            success: false,
        })

        return
    }

    getFirebaseUser(makeRequestToSignInUser, handleError, provider, serviceName)
}

function makeRequestToSignInUser(params: FirebaseSignUpRequest): void {
    axios.post<User | string>('/firebase-sign-in', params)
        .then(resp => handleResponse(resp.data))
        .catch(e => handleServerError(e))
        .finally(() => isLoading.value = false)
}

function handleResponse(user: User | string): void {
    if (typeof user === 'string') {
        showToast({ text: user })
        return
    }

    if (!user) {
        return
    }

    window.location.href = '/user/profile'
}

function handleError(err: any): void {
    console.error(err)
    isLoading.value = false
}
</script>

<template>
    <button
        @click="signIn"
        type="button"
        class="rounded-full w-8 h-8 transition-transform transform hover:scale-110 flex items-center"
        :disabled="isLoading"
    >
        <spinner v-if="isLoading" class="w-8 h-8" />

        <tip v-else :content="`Sign in with ${serviceName}`" class="inline-flex">
            <img
                :src="`/storage/icons/${serviceName}.webp`"
                :alt="`${serviceName} logo`"
                width="100"
                height="100"
                class="w-full"
            />
        </tip>
    </button>
</template>
